import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import axios from 'axios';
import { WHATSAPP_NUMBER } from '../config';

interface FormData {
  nome: string;
  whatsapp: string;
  email: string;
  profissao: string;
  possuiCnpj: string;
  quantidadePessoas: string;
  cidadeEstado: string;
}

const INITIAL_STATE: FormData = {
  nome: '',
  whatsapp: '',
  email: '',
  profissao: '',
  possuiCnpj: '',
  quantidadePessoas: '',
  cidadeEstado: '',
};

const QUANTIDADE_OPTIONS = [
  'Somente eu',
  '2 a 5 pessoas',
  '6 a 15 pessoas',
  'Mais de 15 pessoas',
];

const LeadFormSection: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Obter localização do usuário via API
      let locationData = {};
      try {
        const locationResponse = await axios.get('https://ipapi.co/json/');
        locationData = {
          ip: locationResponse.data.ip,
          city: locationResponse.data.city,
          region: locationResponse.data.region,
          country: locationResponse.data.country_name,
          latitude: locationResponse.data.latitude,
          longitude: locationResponse.data.longitude,
        };
      } catch (error) {
        console.error('Erro ao obter localização:', error);
      }

      // Obter informações do dispositivo
      const deviceData = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        vendor: navigator.vendor,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        language: navigator.language,
      };

      // Obter parâmetros UTM da URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmData = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_term: urlParams.get('utm_term') || '',
        utm_content: urlParams.get('utm_content') || '',
      };

      const mensagem = `Profissão: ${formData.profissao} | Possui CNPJ: ${formData.possuiCnpj} | Pessoas no plano: ${formData.quantidadePessoas} | Cidade/Estado: ${formData.cidadeEstado}`;

      // Enviar dados para o backend
      await axios.post('/api/leads', {
        nome: formData.nome,
        email: formData.email,
        telefone: formData.whatsapp,
        whatsapp: formData.whatsapp,
        profissao: formData.profissao,
        possuiCnpj: formData.possuiCnpj,
        quantidadePessoas: formData.quantidadePessoas,
        cidadeEstado: formData.cidadeEstado,
        mensagem,
        location: locationData,
        device: deviceData,
        utm: utmData,
      });

      setFormData(INITIAL_STATE);

      setSnackbarMessage('Solicitação enviada com sucesso! Um especialista entrará em contato.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Abrir WhatsApp após envio bem-sucedido
      const whatsappMessage = `Olá! Sou ${formData.nome} e gostaria de mais informações sobre planos de saúde empresariais para o meu CNPJ.`;
      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      setSnackbarMessage('Erro ao enviar solicitação. Por favor, tente novamente.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  return (
    <Box
      id="cotacao"
      component="section"
      sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper', scrollMarginTop: { xs: 88, md: 100 } }}
    >
      <Container maxWidth="lg">
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 6 }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'secondary.dark',
              mb: 1.5,
            }}
          >
            Solicitar cotação
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            Descubra suas opções de plano de saúde empresarial.
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ maxWidth: 1040, mx: 'auto' }}>
          <Grid item xs={12} md={7}>
            <Paper
              component={motion.form}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
              variant="outlined"
              sx={{ p: { xs: 3, md: 4.5 }, border: '1px solid', borderColor: 'divider' }}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nome completo"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="WhatsApp"
                    name="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="E-mail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Profissão"
                    name="profissao"
                    placeholder="Ex: Médico(a), Dentista, Fisioterapeuta"
                    value={formData.profissao}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    select
                    fullWidth
                    label="Possui CNPJ?"
                    name="possuiCnpj"
                    value={formData.possuiCnpj}
                    onChange={handleChange}
                  >
                    <MenuItem value="Sim">Sim</MenuItem>
                    <MenuItem value="Não">Não</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    select
                    fullWidth
                    label="Pessoas no plano"
                    name="quantidadePessoas"
                    value={formData.quantidadePessoas}
                    onChange={handleChange}
                  >
                    {QUANTIDADE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Cidade / Estado"
                    name="cidadeEstado"
                    placeholder="Ex: São Paulo / SP"
                    value={formData.cidadeEstado}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Solicitar Minha Cotação'}
                  </Button>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 1.5, textAlign: 'center' }}>
                    Seus dados serão utilizados apenas para o atendimento da sua solicitação.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              sx={{
                height: '100%',
                p: { xs: 3, md: 4 },
                borderRadius: '18px',
                bgcolor: 'primary.dark',
                color: '#fff',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 3 }}>
                Fale diretamente com a MedPrime
              </Typography>

              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <WhatsAppIcon sx={{ color: '#5EEAD4' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>WhatsApp</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                      (11) 96511-5307
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PhoneRoundedIcon sx={{ color: '#5EEAD4' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Telefone</Typography>
                    
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <EmailRoundedIcon sx={{ color: '#5EEAD4' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>E-mail</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                      contato@medprimebeneficios.com.br
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <ScheduleRoundedIcon sx={{ color: '#5EEAD4' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Atendimento</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                      Segunda a Sexta: 9h às 18h
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadFormSection;
